import {FC, ReactNode} from 'react';
import {Grid} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EvStationOutlinedIcon from '@mui/icons-material/EvStationOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ConsumptionTotals} from 'src/types';
import {useBusinessCurrency} from 'src/app/hooks';
import {averagePrice, MoneyAmount, totalCost} from 'src/utils/reportMoney';
import {KpiCard} from '../KpiCard';

interface KpiGridProps {
    data?: ConsumptionTotals;
    loading?: boolean;
}

const formatNumber = (n: number, digits = 0) =>
    n.toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits});

const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours === 0) return `${minutes}m`;
    return `${hours.toLocaleString()}h ${minutes}m`;
};

export const KpiGrid: FC<KpiGridProps> = ({data, loading}) => {
    const intl = useIntl();
    // Only reached when nothing matched at all: with any data the server names the currency itself.
    const fallbackCurrency = useBusinessCurrency();
    // Below md the first figure is the screen's headline; from md up the five cards stay a row.
    const {isCompact} = useScreenTier();

    const consumption = data ? data.consumption / 1000 : 0;
    const sessions = data?.transactionCount ?? 0;
    const chargingTime = data?.chargingTimeSeconds ?? 0;
    const cost = totalCost(data, fallbackCurrency);
    const avgPrice = averagePrice(data, fallbackCurrency);

    const cards: Array<{label: string; value: string | MoneyAmount[]; unit?: string; icon: ReactNode}> = [
        {
            label: intl.formatMessage({id: 'ecTotalConsumption'}),
            value: formatNumber(consumption, 1),
            unit: 'kWh',
            icon: <BoltIcon fontSize="small" />,
        },
        {
            label: intl.formatMessage({id: 'ecTotalCost'}),
            value: cost,
            icon: <PaymentsOutlinedIcon fontSize="small" />,
        },
        {
            label: intl.formatMessage({id: 'ecSessions'}),
            value: formatNumber(sessions),
            icon: <EvStationOutlinedIcon fontSize="small" />,
        },
        {
            label: intl.formatMessage({id: 'ecChargingTime'}),
            value: formatDuration(chargingTime),
            icon: <AccessTimeIcon fontSize="small" />,
        },
        {
            label: intl.formatMessage({id: 'ecAvgPrice'}),
            value: avgPrice,
            icon: <PriceCheckIcon fontSize="small" />,
        },
    ];

    return (
        <Grid container spacing={2}>
            {cards.map((card, index) => {
                // The consumption total, and only it, is promoted on a phone — the same value and the
                // same formatter, given the whole width and the hero type the design draws it at.
                const isHero = isCompact && index === 0;
                return (
                    <Grid item xs={isHero ? 12 : 6} sm={isHero ? 12 : 4} md={2.4} key={card.label}>
                        <KpiCard
                            label={card.label}
                            value={card.value}
                            unit={card.unit}
                            icon={card.icon}
                            loading={loading}
                            hero={isHero}
                        />
                    </Grid>
                );
            })}
        </Grid>
    );
};
